import { Injectable, inject } from '@angular/core';
import {
  DocumentData,
  Firestore,
  Timestamp,
  UpdateData,
  WithFieldValue,
  addDoc,
  collection,
  collectionData,
  deleteDoc,
  doc,
  docData,
  getDocs,
  query,
  updateDoc,
  where,
  writeBatch
} from '@angular/fire/firestore';
import { Project, Status, Visibility } from './interfaces/project';
import { Observable, concat, from, map, of } from 'rxjs';
import {
  ProjectListCacheScope,
  readProjectListCache,
  writeProjectListCache
} from './utils/project-list-cache';

@Injectable({
  providedIn: 'root'
})
export class PortfolioProjectService {
  private readonly firestore = inject(Firestore);
  private readonly projectsCollection = collection(this.firestore, 'projects');

  /** All projects the current user is allowed to read (signed-in users and admins per Firestore rules). */
  getProjects(): Observable<Project[]> {
    return collectionData(this.projectsCollection, { idField: 'id' }).pipe(
      map((projects) => projects.map((project) => this.mapProjectDocument(project)))
    );
  }

  /** Public-only (for anonymous users). Query requires a `visibility` field of `public` on each document. */
  getPublicProjects(): Observable<Project[]> {
    const publicQuery = query(this.projectsCollection, where('visibility', '==', Visibility.public));
    return collectionData(publicQuery, { idField: 'id' }).pipe(
      map((projects) => projects.map((project) => this.mapProjectDocument(project)))
    );
  }

  /**
   * Portfolio grid: admins get a live Firestore stream (edits visible immediately).
   * Other visitors use a one-shot fetch plus localStorage so repeat visits avoid constant reloads.
   */
  getProjectsForPortfolioViewer(isAdmin: boolean, canSeeHidden: boolean): Observable<Project[]> {
    if (isAdmin) {
      return canSeeHidden ? this.getProjects() : this.getPublicProjects();
    }
    const scope: ProjectListCacheScope = canSeeHidden ? 'signedIn' : 'public';
    const cached = readProjectListCache(scope);
    const network$ = from(this.fetchProjectsListSnapshot(canSeeHidden)).pipe(
      map((projects) => {
        writeProjectListCache(scope, projects);
        return projects;
      })
    );
    return cached ? concat(of(cached), network$) : network$;
  }

  private async fetchProjectsListSnapshot(canSeeHidden: boolean): Promise<Project[]> {
    if (canSeeHidden) {
      const snap = await getDocs(this.projectsCollection);
      return snap.docs.map((d) => this.mapProjectDocument({ id: d.id, ...d.data() }));
    }
    const publicQuery = query(this.projectsCollection, where('visibility', '==', Visibility.public));
    const snap = await getDocs(publicQuery);
    return snap.docs.map((d) => this.mapProjectDocument({ id: d.id, ...d.data() }));
  }

  getProjectById(id: string): Observable<Project | undefined> {
    const projectDoc = doc(this.firestore, `projects/${id}`);
    return docData(projectDoc, { idField: 'id' }).pipe(
      map((project) => (project ? this.mapProjectDocument(project) : undefined))
    );
  }

  createProject(project: Omit<Project, 'id'>): Promise<void> {
    return addDoc(this.projectsCollection, this.toFirestorePayload(project)).then(() => undefined);
  }

  updateProject(id: string, project: Omit<Project, 'id'>): Promise<void> {
    const projectDoc = doc(this.firestore, `projects/${id}`);
    return updateDoc(projectDoc, this.toFirestoreUpdatePayload(project)).then(() => undefined);
  }

  deleteProject(id: string): Promise<void> {
    const projectDoc = doc(this.firestore, `projects/${id}`);
    return deleteDoc(projectDoc);
  }

  /**
   * Sets `visibility: public` on legacy docs missing the field so public queries match.
   * Call from admin-only UI (e.g. admin project list once).
   */
  async backfillMissingVisibility(): Promise<void> {
    const snap = await getDocs(this.projectsCollection);
    const batch = writeBatch(this.firestore);
    let count = 0;
    snap.forEach((docSnap) => {
      const data = docSnap.data();
      if (data['visibility'] == null) {
        batch.update(docSnap.ref, { visibility: Visibility.public });
        count++;
      }
    });
    if (count > 0) {
      await batch.commit();
    }
  }

  private mapProjectDocument(project: any): Project {
    const rawVisibility = project.visibility as string | undefined;
    let visibility: Visibility;
    if (rawVisibility === Visibility.hidden) {
      visibility = Visibility.hidden;
    } else if (rawVisibility === Visibility.personal) {
      visibility = Visibility.personal;
    } else {
      visibility = Visibility.public;
    }

    return {
      id: project.id,
      name: project.name ?? '',
      status: this.normalizeStatus(project.status),
      short_description: project.short_description ?? '',
      long_description: project.long_description ?? undefined,
      imageUrl: project.imageUrl ?? undefined,
      imageUrls: Array.isArray(project.imageUrls) ? project.imageUrls : undefined,
      visibility,
      startDate: this.toDate(project.startDate),
      endDate: this.toDate(project.endDate),
      categories: Array.isArray(project.categories) ? project.categories : [],
      tags: Array.isArray(project.tags) ? project.tags : []
    };
  }

  private toFirestorePayload(project: Omit<Project, 'id'>): WithFieldValue<DocumentData> {
    const visibility = project.visibility ?? Visibility.public;
    return {
      name: project.name,
      status: project.status,
      short_description: project.short_description,
      long_description: project.long_description ?? null,
      imageUrl: project.imageUrl ?? null,
      imageUrls: project.imageUrls ?? null,
      visibility,
      startDate: project.startDate ?? null,
      endDate: project.endDate ?? null,
      categories: project.categories ?? [],
      tags: project.tags ?? []
    };
  }

  private toFirestoreUpdatePayload(project: Omit<Project, 'id'>): UpdateData<DocumentData> {
    return this.toFirestorePayload(project) as UpdateData<DocumentData>;
  }

  /** Maps legacy Firestore values to current `Status`. */
  private normalizeStatus(raw: unknown): Status {
    if (raw === 'Inactive' || raw === 'inactive') {
      return Status.planned;
    }
    const values = Object.values(Status) as string[];
    if (typeof raw === 'string' && values.includes(raw)) {
      return raw as Status;
    }
    return Status.active;
  }

  private toDate(value: unknown): Date | undefined {
    if (!value) {
      return undefined;
    }

    if (value instanceof Date) {
      return value;
    }

    if (value instanceof Timestamp) {
      return value.toDate();
    }

    return undefined;
  }
}
